import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { NursingHome } from "@/hooks/use-nursing-home";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, User, GripVertical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

type ContextType = { nursingHome: NursingHome };

interface StaffMember {
  id: string;
  name: string;
  role: string;
  bio: string | null;
  photo_url: string | null;
  display_order: number;
  is_published: boolean;
}

const emptyStaff = {
  name: "",
  role: "",
  bio: "",
  photo_url: "",
  is_published: true,
};

export default function StaffPage() {
  const { nursingHome } = useOutletContext<ContextType>();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyStaff);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (nursingHome?.nh_slug) fetchStaff();
  }, [nursingHome?.nh_slug]);

  const fetchStaff = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("nursing_home_staff")
      .select("*")
      .eq("nursing_home_id", nursingHome.nh_slug)
      .order("display_order", { ascending: true });
    if (!error && data) setStaff(data);
    setLoading(false);
  };

  const openNew = () => {
    setEditingId(null);
    setForm(emptyStaff);
    setDialogOpen(true);
  };

  const openEdit = (s: StaffMember) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      role: s.role,
      bio: s.bio || "",
      photo_url: s.photo_url || "",
      is_published: s.is_published,
    });
    setDialogOpen(true);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fileExt = file.name.split(".").pop();
    const filePath = `staff/${nursingHome.nh_slug}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("public")
      .upload(filePath, file, { upsert: true });

    if (uploadError) {
      toast.error("Failed to upload photo");
      setUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage.from("public").getPublicUrl(filePath);
    setForm({ ...form, photo_url: urlData.publicUrl });
    setUploading(false);
  };

  const handleSave = async () => {
    if (!form.name || !form.role) {
      toast.error("Name and role are required");
      return;
    }
    setSaving(true);
    const payload = {
      nursing_home_id: nursingHome.nh_slug,
      name: form.name,
      role: form.role,
      bio: form.bio || null,
      photo_url: form.photo_url || null,
      is_published: form.is_published,
      display_order: editingId ? undefined : staff.length,
    };

    if (editingId) {
      const { display_order, ...updatePayload } = payload;
      const { error } = await supabase
        .from("nursing_home_staff")
        .update(updatePayload)
        .eq("id", editingId);
      if (error) toast.error("Failed to update");
      else toast.success("Staff member updated");
    } else {
      const { error } = await supabase
        .from("nursing_home_staff")
        .insert(payload);
      if (error) toast.error("Failed to add staff member");
      else toast.success("Staff member added");
    }
    setSaving(false);
    setDialogOpen(false);
    fetchStaff();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Remove this staff member?")) return;
    const { error } = await supabase.from("nursing_home_staff").delete().eq("id", id);
    if (error) toast.error("Failed to delete");
    else {
      toast.success("Staff member removed");
      fetchStaff();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Staff Profiles</h1>
          <p className="text-muted-foreground">Introduce your team to families</p>
        </div>
        <Button onClick={openNew}>
          <Plus className="h-4 w-4 mr-2" />
          Add Staff
        </Button>
      </div>

      {staff.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium">No staff profiles yet</h3>
            <p className="text-muted-foreground mb-4">Adding staff profiles builds trust with families</p>
            <Button onClick={openNew}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Staff Member
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {staff.map((member) => (
            <Card key={member.id}>
              <CardContent className="p-4 flex items-center gap-4">
                <Avatar className="h-14 w-14">
                  <AvatarImage src={member.photo_url || undefined} alt={member.name} />
                  <AvatarFallback>{member.name.split(" ").map(n => n[0]).join("")}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{member.name}</h3>
                    {!member.is_published && (
                      <span className="text-xs bg-muted px-2 py-0.5 rounded">Draft</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{member.role}</p>
                  {member.bio && <p className="text-sm mt-1 line-clamp-2">{member.bio}</p>}
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(member)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => handleDelete(member.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Staff Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Staff Member" : "Add Staff Member"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mary O'Brien" />
            </div>
            <div>
              <Label>Role *</Label>
              <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Director of Nursing" />
            </div>
            <div>
              <Label>Bio</Label>
              <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="A short bio about this team member..." rows={3} />
            </div>
            <div>
              <Label>Photo</Label>
              {form.photo_url && (
                <div className="mb-2">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={form.photo_url} />
                    <AvatarFallback>{form.name?.[0]}</AvatarFallback>
                  </Avatar>
                </div>
              )}
              <Input type="file" accept="image/*" onChange={handlePhotoUpload} disabled={uploading} />
              {uploading && <p className="text-xs text-muted-foreground mt-1">Uploading...</p>}
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} />
              <Label>Published</Label>
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving ? "Saving..." : editingId ? "Update" : "Add Staff Member"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
